import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useGetProfile } from '../../api/auth'
import { Organization, TRANSLATE } from '../../types/organization'
import { StageState } from '../../types/stage-state'
import { AccountRole } from '../../types/account-role'
import { Card, CardContent, CardHeader } from '@mui/material'
import Dropdown from '../common/Dropdown'

interface Props {
  id: string
  number: number
  year: string
  organization: Organization
  createdAt: string
  description?: string
}

const STATE_STYLE = {
  [StageState.BLOCKED]: 'border-zinc-400 text-zinc-400',
  [StageState.IN_PROGRESS]: 'border-primary-500 text-primary-500',
  [StageState.SUBMITTED]: 'border-primary',
  [StageState.ACCEPTED]: 'border-black text-black',
}

const deadline = 30

export default function ProcessCard({
  id,
  createdAt,
  description,
  year,
  number,
  organization,
}: Props) {
  const remainingPercentageCalculated = useMemo(
    () => remainingPercent(createdAt, 30),
    [createdAt, deadline]
  )
  const { data: user } = useGetProfile()

  return (
    <Card
      variant="elevation"
      sx={{ borderWidth: '1px', borderColor: 'primary.main' }}
      className="process-card"
    >
      {user?.role === AccountRole.ADMIN && (
        <CardHeader
          sx={{ pb: 0 }}
          action={<Dropdown options={[{ label: 'دسترسی‌ها', link: `${id}/accesses` }]} />}
        />
      )}
      <CardContent>
        <Link to={`/${id}`}>
          <div className="text-2xl text-center">
            {TRANSLATE[organization] + ' - ' + number.toLocaleString('fa-IR')}
          </div>
        </Link>

        <div className="flex flex-row gap-2 mt-1">
          <div className="text-xs text-zinc-600">
            سال{(+year).toLocaleString('fa-IR', { useGrouping: false })}
          </div>
          <div className="truncate overflow-hidden text-xs text-zinc-500">
            {description && `(${description})`}
          </div>
        </div>
        <div className="process-deadline h-6 w-ful flex flex-row-reverse space-x-5">
          {/* if state is in progress */}
          <>
            <div className="text-right text-xs text-zinc-600">
              <div>مهلت</div>
              {addDays(new Date(createdAt), deadline).toLocaleDateString('fa-IR', {
                dateStyle: 'short',
              })}
            </div>
            <div className="mx-2 self-center flex-grow bg-gray-200 rounded-full h-1 dark:bg-ye-700">
              <div
                className={`bg-${percentColor(
                  remainingPercentageCalculated
                )}  h-1 rounded-full`}
                style={{ width: `${remainingPercentageCalculated}%` }}
              ></div>
            </div>
          </>
        </div>
      </CardContent>
    </Card>
  )
}

function addDays(date: Date, days: number) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function remainingPercent(createdDate: string, deadline: number) {
  const passed = Date.now() - new Date(createdDate).valueOf()
  const all = deadline * 24 * 60 * 60 * 1000
  return +Math.min(100, (100 * passed) / all).toPrecision(1)
}

function percentColor(percent: number) {
  const bucket = Math.floor(percent / 30)
  const colors = ['green-500', 'yellow-400', 'red-500', 'red-800']
  return colors[bucket]
}
